import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useEliminarComentario } from "@/hooks/useFeedback";
import { getApiErrorMessage } from "@/lib/api";
import type { ComentarioDeFeedback } from "@/lib/feedback";

interface EliminarComentarioDialogProps {
  /** Comentario a eliminar; `null` cierra el diálogo. */
  comentario: ComentarioDeFeedback | null;
  onCerrar: () => void;
}

/** Recorta el texto para el título sin cortar a mitad de palabra. */
function resumen(texto: string, tope = 60): string {
  const limpio = texto.trim().replace(/\s+/g, " ");
  if (limpio.length <= tope) return limpio;
  const corte = limpio.slice(0, tope);
  const espacio = corte.lastIndexOf(" ");
  return `${espacio > 20 ? corte.slice(0, espacio) : corte}…`;
}

/**
 * Confirmación de borrado de un comentario.
 *
 * A diferencia del borrado de los catálogos, este es DEFINITIVO: no hay
 * borrado lógico ni forma de restaurarlo, así que el aviso lo dice tal cual.
 * Prometer que "se puede volver a crear igual" sería falso — se perdería el
 * autor original y la fecha.
 */
export function EliminarComentarioDialog({
  comentario,
  onCerrar,
}: EliminarComentarioDialogProps) {
  const [error, setError] = useState<string | null>(null);

  /* Con el diálogo desmontado la alerta de adentro no la ve nadie: ahí el
     toast es la única señal posible. */
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  const eliminar = useEliminarComentario({
    onSuccess: () => {
      toast.success("Comentario eliminado.");
      onCerrar();
    },
    onError: (fallo) => {
      const mensaje = getApiErrorMessage(
        fallo,
        "No pudimos eliminar el comentario. Inténtalo de nuevo.",
      );

      if (montado.current) {
        setError(mensaje);
        return;
      }

      toast.error(mensaje);
    },
  });

  if (!comentario) return null;

  // Pausada NO es en vuelo: si el pedido nunca salió, cerrar es seguro (mismo
  // criterio que `EliminarItemDialog`).
  const enVuelo = eliminar.isPending && !eliminar.isPaused;

  return (
    <AlertDialog
      open
      onOpenChange={(abierto) => {
        if (!abierto && !enVuelo) onCerrar();
      }}
    >
      <AlertDialogContent
        onEscapeKeyDown={(evento) => {
          if (enVuelo) evento.preventDefault();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>¿Eliminar «{resumen(comentario.texto)}»?</AlertDialogTitle>
          <AlertDialogDescription>
            Se borra del tablero para todo el equipo y no se puede recuperar. Si quieres
            corregirlo en vez de borrarlo, usa «Editar».
          </AlertDialogDescription>
        </AlertDialogHeader>

        {error && (
          <div
            role="alert"
            className="bg-danger-soft text-danger-soft-foreground rounded-md px-3.5 py-3 text-sm"
          >
            <p className="font-bold">{error}</p>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={enVuelo}>Cancelar</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={() => {
              setError(null);
              eliminar.mutate(comentario.id);
            }}
            disabled={enVuelo}
            className="gap-2"
          >
            {enVuelo && <Spinner />}
            Eliminar comentario
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
